import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchControllers } from '@/features/controllers/api'
import { fetchSiteInfo } from '@/features/siteInfo/api'
import type { SiteInfoData } from '@/features/siteInfo/types'
import { BackToDetailsLink } from '@/features/deviceDetails/BackToDetailsLink'
import {
  InfoCircleIcon,
  ShieldCheckIcon,
  CpuIcon,
  CloudDownloadIcon,
  SpeedometerIcon,
  ThermometerHalfIcon,
  HddIcon,
  ClockHistoryIcon,
  EthernetIcon,
  MemoryIcon,
  WarningTriangleIcon,
} from '@/features/siteInfo/icons'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/site_info_content.html's visual design one-for-one (same
 * three-column health/version/platform overview, same "Available Software
 * Versions" badge list, same four-tile System Metrics row with color-coded
 * thresholds, same Memory Information row, same Device ID/Last Event
 * footer card, same "No Site Information Available" empty state) - this is
 * the one legacy page the user explicitly asked to keep the original look
 * of, rather than the app's usual plain-card treatment. A `?controller_id=`
 * query param preselects the controller, matching the legacy per-device
 * sidebar's direct link.
 */
export default function SiteInfoPage() {
  const controllersQuery = useQuery({ queryKey: ['controllers'], queryFn: fetchControllers })
  // Read directly from the URL, not frozen into local state at mount -- see
  // SettingsAlarmsPage's matching comment for the real bug this fixes
  // (switching controllers here previously didn't touch the URL, so the
  // browser's Back button/refresh/share still pointed at whichever
  // controller was selected when the page first loaded).
  const [searchParams, setSearchParams] = useSearchParams()
  const controllerId = searchParams.get('controller_id') ?? ''
  // The site the user actually navigated here FROM -- see SettingsAlarmsPage's
  // matching comment. "← Back to site details" always returns to this one,
  // never whichever site the dropdown currently shows.
  const [originalControllerId] = useState(() => searchParams.get('controller_id') ?? '')

  const siteInfoQuery = useQuery({
    queryKey: ['site-info', controllerId],
    queryFn: () => fetchSiteInfo(controllerId),
    enabled: Boolean(controllerId),
  })

  const controllerName =
    (controllersQuery.data ?? []).find((c) => c.controller_id === controllerId)?.controller_name || controllerId

  return (
    <div className="space-y-4">
      <BackToDetailsLink controllerId={originalControllerId} />

      <div className="rounded border border-border bg-card p-4">
        <label className="mb-1 block text-xs text-text-secondary">Controller</label>
        <select
          value={controllerId}
          onChange={(e) => setSearchParams(e.target.value ? { controller_id: e.target.value } : {})}
          className="w-full max-w-sm rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
        >
          <option value="">Select a controller…</option>
          {(controllersQuery.data ?? []).map((c) => (
            <option key={c.controller_id} value={c.controller_id}>
              {c.controller_name || c.controller_id}
            </option>
          ))}
        </select>
      </div>

      {!controllerId && <p className="text-sm text-text-secondary">Select a controller to view its site information.</p>}
      {controllerId && siteInfoQuery.isLoading && <LoadingState label="Loading site info…" />}
      {controllerId && siteInfoQuery.isError && (
        <ErrorState
          message={siteInfoQuery.error instanceof Error ? siteInfoQuery.error.message : 'Failed to load.'}
          onRetry={() => siteInfoQuery.refetch()}
        />
      )}

      {controllerId && siteInfoQuery.data && (
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <span className="text-brand">
              <InfoCircleIcon />
            </span>
            <h1 className="text-lg font-semibold text-text-primary">Site Information - {controllerName}</h1>
          </div>

          {siteInfoQuery.data.site_info ? (
            <SiteInfoBody info={siteInfoQuery.data.site_info} deviceId={siteInfoQuery.data.controller.controller_id} />
          ) : (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <span style={{ color: '#f59e0b' }}>
                <WarningTriangleIcon />
              </span>
              <h2 className="text-lg font-semibold" style={{ color: '#f59e0b' }}>
                No Site Information Available
              </h2>
              <p className="text-sm text-text-secondary">Site information for this controller has not been received yet.</p>
              <p className="text-xs text-text-tertiary">
                Data will appear here once the Lambda function processes the first system status message from this device.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SiteInfoBody({ info, deviceId }: { info: SiteInfoData; deviceId: string }) {
  const healthy = Boolean(info.system_healthy)
  const healthColor = healthy ? 'var(--success)' : '#f59e0b'
  const cpuTemp = typeof info.cpu_temp === 'number' ? info.cpu_temp : undefined
  const cpuUsage = typeof info.cpu_usage_percent === 'number' ? info.cpu_usage_percent : undefined
  const cpuTempColor = cpuTemp !== undefined && cpuTemp > 70 ? 'var(--error)' : cpuTemp !== undefined && cpuTemp > 60 ? '#f59e0b' : 'var(--success)'
  const cpuUsageColor = cpuUsage !== undefined && cpuUsage > 80 ? 'var(--error)' : cpuUsage !== undefined && cpuUsage > 60 ? '#f59e0b' : 'var(--blue)'
  const memoryInfo = info.memory_info as Record<string, unknown> | undefined
  const versions = info.software_version_available ?? []

  return (
    <div className="space-y-6 p-4">
      {/* System Status Overview */}
      <div className="grid grid-cols-1 gap-6 text-center sm:grid-cols-3">
        <div>
          <h2 className="mb-2 text-base font-medium text-text-primary">System Health</h2>
          <div className="flex justify-center" style={{ color: healthColor }}>
            <ShieldCheckIcon />
          </div>
          <h3 className="mt-2 text-lg font-semibold" style={{ color: healthColor }}>
            {healthy ? 'Healthy' : 'Warning'}
          </h3>
        </div>
        <div>
          <h2 className="mb-2 text-base font-medium text-brand">Software Version</h2>
          <p className="text-xl font-bold text-text-primary">{info.software_version || 'N/A'}</p>
          <p className="mt-1 text-xs text-text-tertiary">Last updated: {info.updated_at_display || 'N/A'}</p>
        </div>
        <div>
          <h2 className="mb-2 text-base font-medium text-text-primary">Platform</h2>
          <div className="flex justify-center text-accent">
            <CpuIcon />
          </div>
          <p className="mt-1 font-semibold text-text-primary">{info.platform || 'Unknown'}</p>
        </div>
      </div>

      <hr className="border-border" />

      {/* Available Software Versions */}
      {versions.length > 0 && (
        <div className="rounded border border-border">
          <div className="flex items-center gap-2 border-b border-border bg-bg-secondary px-4 py-2">
            <span className="text-brand">
              <CloudDownloadIcon />
            </span>
            <h3 className="text-sm font-semibold text-text-primary">Available Software Versions</h3>
          </div>
          <div className="flex flex-wrap gap-2 p-4">
            {versions.map((version) => {
              const current = version === info.software_version
              return (
                <span
                  key={version}
                  className={`rounded px-2 py-1 text-xs font-medium ${
                    current ? 'bg-brand text-white' : 'bg-bg-tertiary text-text-secondary'
                  }`}
                >
                  {current ? '✓ ' : ''}
                  {version}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* System Metrics */}
      <div className="rounded border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-bg-secondary px-4 py-2">
          <span className="text-accent">
            <SpeedometerIcon />
          </span>
          <h3 className="text-sm font-semibold text-text-primary">System Metrics</h3>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricTile
            label="CPU Temperature"
            value={info.cpu_temp_display || 'N/A'}
            color={cpuTempColor}
            icon={<ThermometerHalfIcon />}
          />
          <MetricTile
            label="CPU Usage"
            value={info.cpu_usage_display || 'N/A'}
            color={cpuUsageColor}
            icon={<HddIcon />}
          />
          <MetricTile label="Runtime" value={info.runtime_display || 'N/A'} color="var(--blue)" icon={<ClockHistoryIcon />} />
          <MetricTile
            label="Local IP"
            value={info.controller_local_ip || 'N/A'}
            color="var(--text-secondary)"
            icon={<EthernetIcon />}
          />
        </div>

        {memoryInfo && (
          <>
            <hr className="border-border" />
            <div className="p-4">
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-secondary">
                <MemoryIcon />
                Memory Information
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                <p>
                  <strong className="text-text-primary">RAM Free:</strong>{' '}
                  <span className="text-text-secondary">{info.memory_free_display || 'N/A'}</span>
                </p>
                <p>
                  <strong className="text-text-primary">RAM Used:</strong>{' '}
                  <span className="text-text-secondary">{info.memory_used_display || 'N/A'}</span>
                </p>
                <p>
                  <strong className="text-text-primary">Disk Free:</strong>{' '}
                  <span className="text-text-secondary">{info.disk_free_display || 'N/A'}</span>
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Device ID and Timestamps */}
      <div className="rounded border border-border bg-bg-secondary p-4 text-sm">
        <p className="mb-1">
          <strong className="text-text-primary">Device ID:</strong>{' '}
          <code className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs">{deviceId}</code>
        </p>
        <p className="text-xs text-text-tertiary">
          <strong>Last Event:</strong> {info.event_date_display || 'N/A'}
        </p>
      </div>
    </div>
  )
}

function MetricTile({ label, value, color, icon }: { label: string; value: string; color: string; icon: React.ReactNode }) {
  return (
    <div className="rounded border p-3 text-center" style={{ borderColor: color }}>
      <h4 className="mb-1 text-xs text-text-secondary">{label}</h4>
      <p className="text-lg font-bold" style={{ color }}>
        {value}
      </p>
      <div className="mt-1 flex justify-center" style={{ color }}>
        {icon}
      </div>
    </div>
  )
}
